import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Shield, Mail, ArrowLeft, Loader2, CheckCircle, Copy, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import api from '@/lib/api';

export function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [resetLink, setResetLink] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    
    try {
      const response = await api.post('/auth/forgot-password', { email });
      setSuccess(true);
      
      // Demo mode: show reset link
      if (response.data.demo_reset_link) {
        setResetLink(window.location.origin + response.data.demo_reset_link);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao solicitar recuperação de senha');
    } finally {
      setIsLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(resetLink);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-8 bg-background grid-bg" data-testid="forgot-password-page">
      <Card className="w-full max-w-md bg-card/50 backdrop-blur-xl border-border">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Shield className="h-8 w-8 text-success" />
            <span className="font-display font-bold text-2xl uppercase">InfraShield</span>
          </div>
          <CardTitle className="font-display text-xl uppercase tracking-wider">
            Recuperar Senha
          </CardTitle>
          <CardDescription>
            Digite seu email para receber instruções de recuperação
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {!success ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    className="pl-9"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="forgot-email"
                  />
                </div>
              </div>
              
              <Button 
                type="submit" 
                className="w-full font-display uppercase tracking-wider"
                disabled={isLoading}
                data-testid="forgot-submit"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  'Enviar Instruções'
                )}
              </Button>
              
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar para o login
                </Link>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <CheckCircle className="h-12 w-12 text-success mx-auto mb-4" />
                <p className="text-sm text-muted-foreground">
                  Se o email existir em nossa base, você receberá as instruções de recuperação.
                </p>
              </div>
              
              {/* Demo mode: show reset link */}
              {resetLink && (
                <Alert className="bg-primary/10 border-primary/30">
                  <AlertDescription>
                    <p className="text-xs text-muted-foreground mb-2 font-display uppercase">
                      Modo Demo - Link de recuperação:
                    </p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs bg-background p-2 rounded overflow-x-auto">
                        {resetLink}
                      </code>
                      <Button size="icon" variant="ghost" onClick={copyToClipboard}>
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                    <Link to={resetLink.replace(window.location.origin, '')} className="mt-2 inline-block">
                      <Button size="sm" className="mt-2">
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Usar Link
                      </Button>
                    </Link>
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="text-center">
                <Link 
                  to="/login" 
                  className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Voltar para o login
                </Link>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default ForgotPasswordPage;
